import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, History, Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Video, MessageSquare, MessageCircle, Bot, ChevronRight, ChevronDown,
  Voicemail, Clock, User as UserIcon, MapPin, Search as SearchIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

/**
 * /admin/master-history — platform-wide audit log.
 *
 * Top filter row mimics the AI character chip layout: circular icon + label
 * underneath, horizontally scrollable on mobile.
 *
 * Filters (in order): Internal Calls · External Calls · Video Calls · Team Chat · External Text · AI Chats
 *
 * Each list row is expandable. Internal/Video calls expand to show transcript
 * + AI summary. Team chat / external text / AI chat expand to show full
 * threaded message history (loaded lazily on expand).
 */

const FILTERS = [
  { key: 'internal-calls', label: 'Internal Calls',  icon: PhoneCall,    color: '#586D52' },
  { key: 'external-calls', label: 'External Calls',  icon: PhoneIncoming,color: '#3b82f6' },
  { key: 'video-calls',    label: 'Video Calls',     icon: Video,        color: '#8b5cf6' },
  { key: 'team-chats',     label: 'Team Chat',       icon: MessageSquare,color: '#10b981' },
  { key: 'external-texts', label: 'External Texts',  icon: MessageCircle,color: '#f59e0b' },
  { key: 'ai-chats',       label: 'AI Chats',        icon: Bot,          color: '#ef4444' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};
const fmtDuration = (sec) => {
  if (!sec || sec < 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

// =============== Row components ===============

const CallRow = ({ item, onToggle, expanded, kind }) => {
  const Icon = item.status === 'missed' ? PhoneMissed : (kind === 'video-calls' ? Video : PhoneCall);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70" data-testid={`call-row-${item.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left">
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
        <Icon className={`w-5 h-5 flex-shrink-0 ${item.status === 'missed' ? 'text-red-500' : 'text-[#586D52]'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {item.caller?.name || 'Unknown'} → {item.callee?.name || 'Unknown'}
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Clock className="w-3 h-3" />
            {fmtDate(item.started_at)}
            {fmtDuration(item.duration_seconds) && <span>· {fmtDuration(item.duration_seconds)}</span>}
            <span className="capitalize">· {item.status || 'unknown'}</span>
          </p>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-gray-100 dark:border-gray-800">
          {item.ai_summary ? (
            <div className="text-sm">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">AI Summary</p>
              <p className="whitespace-pre-wrap">{item.ai_summary}</p>
            </div>
          ) : <p className="text-xs text-muted-foreground">No AI summary linked to this call yet.</p>}
          {item.transcript ? (
            <div className="text-sm">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Transcript</p>
              <pre className="whitespace-pre-wrap font-sans text-xs bg-gray-50 dark:bg-[#0d0d0d] p-2 rounded-md max-h-72 overflow-auto">{typeof item.transcript === 'string' ? item.transcript : JSON.stringify(item.transcript, null, 2)}</pre>
            </div>
          ) : <p className="text-xs text-muted-foreground">No transcript available.</p>}
          {item.ended_with_voicemail && item.voicemail_url && (
            <a href={item.voicemail_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#586D52] hover:underline">
              <Voicemail className="w-3.5 h-3.5" />
              Play voicemail
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const ExternalCallRow = ({ item, onToggle, expanded }) => {
  const Icon = item.status === 'missed' || item.ended_with_voicemail ? PhoneMissed : (item.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing);
  const fromTo = item.direction === 'inbound'
    ? `${item.contact?.name || item.from_number || 'Unknown'} → ${item.routed_to?.name || 'Unassigned'}`
    : `${item.routed_to?.name || 'Unknown'} → ${item.contact?.name || item.to_number || 'Unknown'}`;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70" data-testid={`extcall-row-${item.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left">
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
        <Icon className={`w-5 h-5 flex-shrink-0 ${(item.status === 'missed' || item.ended_with_voicemail) ? 'text-red-500' : 'text-[#3b82f6]'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{fromTo}</p>
          <p className="text-xs text-muted-foreground flex items-center flex-wrap gap-x-2 gap-y-0.5">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(item.started_at)}</span>
            {fmtDuration(item.duration_seconds) && <span>· {fmtDuration(item.duration_seconds)}</span>}
            <span className="capitalize">· {item.direction || ''} {item.status || ''}</span>
            {item.state && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.state}</span>}
            <span className={`px-1.5 rounded uppercase tracking-wider text-[10px] ${item.contact?.assignment_type === 'unassigned' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{item.contact?.assignment_type === 'unassigned' ? 'unassigned' : 'assigned'}</span>
          </p>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-gray-100 dark:border-gray-800 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs break-words">
            <div className="break-all"><span className="text-muted-foreground">From:</span> {item.from_number || '—'}</div>
            <div className="break-all"><span className="text-muted-foreground">To:</span> {item.to_number || '—'}</div>
            <div className="break-words"><span className="text-muted-foreground">Routed to:</span> {item.routed_to?.name || (item.contact?.assignment_type === 'unassigned' ? 'Unassigned (voicemail)' : '—')}</div>
            <div className="break-all"><span className="text-muted-foreground">Twilio SID:</span> <span className="font-mono">{item.twilio_call_sid || '—'}</span></div>
          </div>
          {item.ai_summary ? (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">AI Summary</p>
              <p className="whitespace-pre-wrap">{item.ai_summary}</p>
            </div>
          ) : <p className="text-xs text-muted-foreground">No AI summary linked to this call yet.</p>}
          {item.transcript ? (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Transcript</p>
              <pre className="whitespace-pre-wrap font-sans text-xs bg-gray-50 dark:bg-[#0d0d0d] p-2 rounded-md max-h-72 overflow-auto">{typeof item.transcript === 'string' ? item.transcript : JSON.stringify(item.transcript, null, 2)}</pre>
            </div>
          ) : <p className="text-xs text-muted-foreground">No transcript available.</p>}
          {item.voicemail_recording_url && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Voicemail</p>
              <audio src={item.voicemail_recording_url} controls className="w-full" />
              {item.voicemail_duration ? <p className="text-[10px] text-muted-foreground">{item.voicemail_duration}s</p> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ThreadRow = ({ title, subtitle, meta, expanded, onToggle, children, testId }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70" data-testid={testId}>
    <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left">
      {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
        <UserIcon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
        {meta && <p className="text-[11px] text-muted-foreground mt-0.5">{meta}</p>}
      </div>
    </button>
    {expanded && (
      <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800">
        {children}
      </div>
    )}
  </div>
);

// =============== Main page ===============

const MasterHistoryPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [filter, setFilter] = useState('internal-calls');
  const [data, setData] = useState({});      // { filterKey: items[] }
  const [loading, setLoading] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [threadCache, setThreadCache] = useState({}); // key -> messages
  usePageHeader({ title: 'History', back: '/admin/calling' }, []);

  const isAdmin = user?.role === 'admin';

  // Per-filter search query so each tab keeps its own input.
  const [search, setSearch] = useState({});
  const setQ = (key, v) => setSearch((s) => ({ ...s, [key]: v }));
  const q = (search[filter] || '').trim().toLowerCase();

  const load = async (key) => {
    if (loading[key] || data[key]) return;
    setLoading((s) => ({ ...s, [key]: true }));
    try {
      const res = await api.get(`/admin/master-history/${key}`);
      setData((s) => ({ ...s, [key]: res.data?.items || [] }));
    } catch (e) {
      toast.error(`Could not load ${key}`);
    } finally {
      setLoading((s) => ({ ...s, [key]: false }));
    }
  };
  useEffect(() => { if (isAdmin) load(filter); /* eslint-disable-next-line */ }, [filter, isAdmin]);

  const toggleExpand = (rowKey) => setExpanded((cur) => (cur === rowKey ? null : rowKey));

  const loadThread = async (cacheKey, url) => {
    if (threadCache[cacheKey]) return;
    try {
      const res = await api.get(url);
      setThreadCache((s) => ({ ...s, [cacheKey]: res.data?.messages || [] }));
    } catch (e) {
      toast.error('Could not load thread');
    }
  };

  const items = data[filter] || [];
  const isLoading = !!loading[filter];

  // Filter rows by the active search query — checks contact name, user
  // name, and phone number across whichever shape the row uses.
  const filteredItems = useMemo(() => {
    if (!q) return items;
    const has = (s) => s && String(s).toLowerCase().includes(q);
    return items.filter((it) => {
      if (filter === 'internal-calls' || filter === 'video-calls') {
        return has(it.caller?.name) || has(it.callee?.name)
          || has(it.caller?.phone) || has(it.callee?.phone);
      }
      if (filter === 'external-calls') {
        return has(it.contact?.name) || has(it.routed_to?.name)
          || has(it.from_number) || has(it.to_number);
      }
      if (filter === 'team-chats') {
        return (it.participants || []).some((p) => has(p.name))
          || has(it.last_message);
      }
      if (filter === 'external-texts') {
        return has(it.contact_name) || has(it.user?.name) || has(it.phone_number);
      }
      if (filter === 'ai-chats') {
        return has(it.user?.name) || has(it.character) || has(it.last_user_message);
      }
      return true;
    });
  }, [items, q, filter]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="master-history-page">
      <div className="max-w-3xl mx-auto p-3">
        {/* Filter chips — stretched evenly across the row, no horizontal scroll */}
        <div className="grid grid-cols-6 gap-1 sm:gap-1.5 mb-3 w-full">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className="flex flex-col items-center gap-1 min-w-0 w-full"
                data-testid={`filter-${f.key}`}
              >
                <div
                  className={`aspect-square w-full max-w-[56px] mx-auto rounded-full flex items-center justify-center transition-all border-2 ${
                    isActive ? 'border-[#586D52] scale-105 shadow-md' : 'border-transparent bg-white dark:bg-[#1a1a1a]'
                  }`}
                  style={isActive ? { backgroundColor: f.color } : {}}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-white' : 'text-muted-foreground'}`} />
                </div>
                <span className={`text-[9px] sm:text-[11px] font-medium text-center leading-tight w-full truncate ${isActive ? 'text-[#586D52]' : 'text-muted-foreground'}`}>
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Per-filter search bar — sits directly under the chip row so it
            scopes to whichever filter is active. */}
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search[filter] || ''}
            onChange={(e) => setQ(filter, e.target.value)}
            placeholder="Search by contact, user, or phone…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/70 dark:bg-[#1a1a1a]/70 border border-gray-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#586D52]"
            data-testid={`history-search-${filter}`}
          />
        </div>

        <Card className="rounded-2xl">
          <CardContent className="pt-4 space-y-2">
            {isLoading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
            {!isLoading && filteredItems.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">{q ? 'No matches.' : 'No records yet.'}</p>
            )}
            {!isLoading && filteredItems.map((item) => {
              if (filter === 'internal-calls' || filter === 'video-calls') {
                return (
                  <CallRow
                    key={item.id}
                    item={item}
                    expanded={expanded === item.id}
                    onToggle={() => toggleExpand(item.id)}
                    kind={filter}
                  />
                );
              }
              if (filter === 'external-calls') {
                return <ExternalCallRow key={item.id} item={item} expanded={expanded === item.id} onToggle={() => toggleExpand(item.id)} />;
              }
              if (filter === 'team-chats') {
                const isOpen = expanded === item.id;
                const cacheKey = `team:${item.id}`;
                const messages = threadCache[cacheKey];
                const title = item.is_group ? `Group · ${(item.participants || []).map((p) => p.name).join(', ')}` : (item.participants || []).map((p) => p.name).join(' ↔ ');
                return (
                  <ThreadRow
                    key={item.id}
                    testId={`teamchat-row-${item.id}`}
                    title={title || 'Conversation'}
                    subtitle={item.last_message || '—'}
                    meta={fmtDate(item.last_message_at) + ` · ${(item.participants || []).length} participants`}
                    expanded={isOpen}
                    onToggle={() => {
                      toggleExpand(item.id);
                      if (!isOpen) loadThread(cacheKey, `/admin/master-history/team-chats/${item.id}/messages`);
                    }}
                  >
                    {!messages ? <p className="text-xs text-muted-foreground py-2">Loading messages…</p> : (
                      <div className="space-y-1.5 max-h-80 overflow-auto">
                        {messages.map((m) => (
                          <div key={m.id} className="text-xs">
                            <span className="font-medium">{m.sender?.name || 'Unknown'}</span>
                            <span className="text-muted-foreground ml-1.5">{fmtDate(m.timestamp)}</span>
                            <p className="whitespace-pre-wrap">{m.content || ''}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ThreadRow>
                );
              }
              if (filter === 'external-texts') {
                const isOpen = expanded === item.contact_id;
                const cacheKey = `ext:${item.contact_id}`;
                const messages = threadCache[cacheKey];
                return (
                  <ThreadRow
                    key={item.contact_id}
                    testId={`exttext-row-${item.contact_id}`}
                    title={`${item.contact_name || item.phone_number} ↔ ${item.user?.name || 'Unknown'}`}
                    subtitle={item.last_message || '—'}
                    meta={`${fmtDate(item.last_message_at)} · ${item.message_count} msg${item.message_count === 1 ? '' : 's'}${item.state ? ' · ' + item.state : ''}`}
                    expanded={isOpen}
                    onToggle={() => {
                      toggleExpand(item.contact_id);
                      if (!isOpen) loadThread(cacheKey, `/admin/master-history/external-texts/${item.contact_id}/messages`);
                    }}
                  >
                    {!messages ? <p className="text-xs text-muted-foreground py-2">Loading messages…</p> : (
                      <div className="space-y-1.5 max-h-80 overflow-auto">
                        {messages.map((m) => (
                          <div key={m.id} className={`text-xs flex flex-col ${m.direction === 'inbound' ? 'items-start' : 'items-end'}`}>
                            <div className={`px-2 py-1 rounded-lg max-w-[85%] min-w-0 ${m.direction === 'inbound' ? 'bg-gray-100 dark:bg-[#0d0d0d]' : 'bg-[#586D52]/15'}`}>
                              <p className="whitespace-pre-wrap break-words">{m.body}</p>
                              <p className="text-[10px] text-muted-foreground break-all">
                                {m.direction === 'inbound' ? `${item.contact_name || m.phone_number}` : (m.user?.name || '')} · {fmtDate(m.created_at)}
                                {m.twilio_sid ? ` · ${m.twilio_sid}` : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ThreadRow>
                );
              }
              if (filter === 'ai-chats') {
                const rowKey = `${item.user?.id}:${item.character}:${item.session_id}`;
                const isOpen = expanded === rowKey;
                const cacheKey = `ai:${rowKey}`;
                const messages = threadCache[cacheKey];
                return (
                  <ThreadRow
                    key={rowKey}
                    testId={`aichat-row-${rowKey}`}
                    title={`${item.user?.name || 'Unknown'} · ${item.character || 'orb'}`}
                    subtitle={item.last_user_message || '—'}
                    meta={`${fmtDate(item.last_message_at)} · ${item.message_count} message${item.message_count === 1 ? '' : 's'}`}
                    expanded={isOpen}
                    onToggle={() => {
                      toggleExpand(rowKey);
                      if (!isOpen) loadThread(cacheKey, `/admin/master-history/ai-chats/${item.user?.id}/${item.character}/${item.session_id}/messages`);
                    }}
                  >
                    {!messages ? <p className="text-xs text-muted-foreground py-2">Loading messages…</p> : (
                      <div className="space-y-2 max-h-96 overflow-auto">
                        {messages.map((m) => (
                          <div key={m.id} className="text-xs">
                            <p className="text-[10px] text-muted-foreground">{fmtDate(m.created_at)}</p>
                            <div className="bg-gray-100 dark:bg-[#0d0d0d] rounded p-2 mb-1">
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">User</p>
                              <p className="whitespace-pre-wrap">{m.user_message}</p>
                            </div>
                            <div className="bg-[#586D52]/10 rounded p-2">
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{item.character}</p>
                              <p className="whitespace-pre-wrap">{m.ai_response}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ThreadRow>
                );
              }
              return null;
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MasterHistoryPage;
